import type { ReactNode } from 'react';
import './FormulaBlock.css';

export interface FormulaBlockProps {
  children: ReactNode;
  /**
   * `formula` scrolls horizontally; `path` breaks anywhere. A formula loses its
   * meaning when wrapped mid-expression, whereas an S3 URI or a base32 secret has no
   * meaning to lose and must never force the page to scroll.
   */
  wrap?: 'formula' | 'path';
  /** `danger` is the error-detail form — a white block with a danger border. */
  tone?: 'default' | 'danger';
  /**
   * An accessible name, e.g. "Impact formula". Without one this is an unlabelled
   * scroll region, and a keyboard user tabbing into it hears nothing about what they
   * have landed in.
   */
  label?: string;
}

/**
 * Monospace inset for a formula, a path or an error (catalogue, Tier 3).
 *
 * **6 of the prototype's 18 page files** — the impact formula, per-finding formulas,
 * an `AccessDenied` detail, an MFA secret, manifest values, a resolved S3 path
 * (prototype/CENSUS.md §2a item 15).
 *
 * 08 §7 requires every derived figure to disclose how it was produced, so this is
 * the component that requirement lands in: it exists so a screen showing "€48,200"
 * has somewhere to put the arithmetic, rather than a tooltip nobody reads.
 *
 * `Card.__inset` is the right *surface* at the wrong padding and radius, and is not
 * monospaced — which is why this is separate rather than a Card variant.
 */
export function FormulaBlock({ children, wrap = 'formula', tone = 'default', label }: FormulaBlockProps) {
  const scrolls = wrap === 'formula';
  return (
    <pre
      className={`dpr-formula dpr-formula--${wrap} dpr-formula--${tone}`}
      /* A horizontally-scrolling region must be reachable and named, per WCAG 2.1.1
         — otherwise its overflowed content is unreachable by keyboard. A wrapping
         block has nothing to scroll, so it stays out of the tab order. */
      {...(scrolls ? { tabIndex: 0, role: 'region', 'aria-label': label } : {})}
    >
      {children}
    </pre>
  );
}
